import React, { useEffect, useState, useCallback } from 'react';
import { Seo } from '../components/Seo';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth, formatApiErrorDetail } from '@/context/AuthContext';
import { toast } from 'sonner';
import usePageTitle from '@/hooks/usePageTitle';
import AdminJurorsTab from '@/components/AdminJurorsTab';
import AdminCategoriesTab from '@/components/AdminCategoriesTab';
import AdminPaymentsTab, { MarkPaidModal } from '@/components/AdminPaymentsTab';
import AdminScoringTab from '@/components/AdminScoringTab';
import AdminFrameworksTab from '@/components/AdminFrameworksTab';
import AdminSupportingFiles from '@/components/AdminSupportingFiles';
import AdminSubmitOnBehalfModal from '@/components/AdminSubmitOnBehalfModal';
import AdminSettingsTab from '@/components/AdminSettingsTab';
import AdminCMSTab from '@/components/AdminCMSTab';
import AdminUsersTab from '@/components/AdminUsersTab';
import AdminWinnersTab from '@/components/AdminWinnersTab';
import { downloadCSV as csvExport, downloadXLSX } from '@/lib/exports';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const axiosAuth = axios.create({ withCredentials: true });

const TABS = [
  { key: 'nominations', label: 'Nominations' },
  { key: 'nominators', label: 'Registrations' },
  { key: 'payments', label: 'Payments' },
  { key: 'scoring', label: 'Jury Scoring' },
  { key: 'frameworks', label: 'Evaluation Frameworks' },
  { key: 'categories', label: 'Categories' },
  { key: 'jurors', label: 'Jurors' },
  { key: 'sponsors', label: 'Sponsors' },
  { key: 'subscribers', label: 'Subscribers' },
  { key: 'contacts', label: 'Contacts' },
  { key: 'cms', label: 'CMS' },
  { key: 'winners', label: 'Winners' },
  { key: 'admins', label: 'Admins' },
  { key: 'settings', label: 'Settings' },
];

const CUSTOM_TABS = new Set(['categories', 'payments', 'scoring', 'frameworks', 'jurors', 'nominators', 'cms', 'winners', 'admins', 'settings']);

const FRIENDLY_LABEL = {
  entrant_type: 'Entrant Type',
  organisation_name: 'Organisation',
  first_name: 'First Name',
  last_name: 'Last Name',
  designation: 'Designation',
  email: 'Email',
  mobile: 'Mobile',
  website: 'Website',
  linkedin: 'LinkedIn',
  category: 'Primary Category',
  additional_categories: 'Additional Categories',
  entry_title: 'Entry Title',
  brand_name: 'Brand',
  campaign_start: 'Campaign Start',
  campaign_end: 'Campaign End',
  primary_market: 'Primary Market',
  campaign_summary: 'Campaign Summary',
  business_challenge: 'Business Challenge',
  strategy_idea: 'Strategy / Idea',
  execution: 'Execution',
  elevator_pitch: 'Elevator Pitch',
  campaign_objectives: 'Campaign Objectives',
  key_results: 'Key Results',
  business_impact: 'Business Impact',
  credits_brand: 'Credits — Brand',
  credits_creative: 'Credits — Creative',
  credits_media: 'Credits — Media',
  credits_digital: 'Credits — Digital',
  credits_influencer: 'Credits — Influencer',
  credits_pr: 'Credits — PR',
  credits_production: 'Credits — Production',
  credits_tech: 'Credits — Tech',
  credits_other: 'Credits — Other',
  case_study_deck_url: 'Case Study Deck (URL)',
  case_study_deck_name: 'Case Study Deck (File)',
  supporting_material_link: 'Supporting Material Link',
  previous_recognition: 'Previous Recognition',
  brand_approval_url: 'Brand Approval (URL)',
  brand_approval_name: 'Brand Approval (File)',
  declaration_agreed: 'Declaration Agreed',
  full_name: 'Full Name',
  phone: 'Phone',
  company: 'Company',
  interest: 'Interest',
  budget: 'Budget',
  message: 'Message',
  handle: 'Handle',
  channel: 'Channel',
  name: 'Name',
  purpose: 'Purpose',
  created_at: 'Submitted At',
  id: 'ID',
};

const NOMINATION_EDITABLE = [
  'entrant_type', 'organisation_name', 'first_name', 'last_name', 'designation', 'email', 'mobile',
  'website', 'linkedin', 'category', 'entry_title', 'brand_name', 'campaign_summary',
  'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch', 'campaign_objectives',
  'business_impact', 'supporting_material_link', 'previous_recognition',
];

const SPONSOR_EDITABLE = ['full_name', 'email', 'phone', 'company', 'designation', 'interest', 'budget', 'message'];
const SUBSCRIBER_EDITABLE = ['handle', 'channel'];

const fmtDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' });
  } catch {
    return iso;
  }
};

const downloadCSV = (filename, rows) => {
  if (!rows.length) {
    toast.error('Nothing to export.');
    return;
  }
  const allKeys = Array.from(rows.reduce((s, r) => { Object.keys(r).forEach((k) => s.add(k)); return s; }, new Set()));
  const escape = (v) => {
    if (v == null) return '';
    let s = typeof v === 'object' ? JSON.stringify(v) : String(v);
    if (s.includes('"') || s.includes(',') || s.includes('\n')) {
      s = '"' + s.replace(/"/g, '""') + '"';
    }
    return s;
  };
  const csv = [allKeys.join(','), ...rows.map((r) => allKeys.map((k) => escape(r[k])).join(','))].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = filename;
  link.click();
  URL.revokeObjectURL(link.href);
};

const StatCard = ({ label, value, testId, onClick }) => {
  const clickable = typeof onClick === 'function';
  const Wrapper = clickable ? 'button' : 'div';
  return (
    <Wrapper
      type={clickable ? 'button' : undefined}
      onClick={onClick}
      className={`text-left w-full border border-[var(--gold-faint)] bg-[var(--bg-alt)]/40 p-6 transition-colors ${clickable ? 'cursor-pointer hover:border-[var(--gold)] hover:bg-[var(--bg-alt)]/70 focus:outline-none focus:border-[var(--gold)]' : ''}`}
      data-testid={testId}
    >
      <Seo title="Admin Panel · AdAura Awards" description="Private area — not indexed." path="/" noindex />
      <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] flex items-center gap-2">
        {label}
        {clickable && <span className="text-[8px] tracking-[0.25em] text-[var(--gold)]/70">↗</span>}
      </div>
      <div className="font-[Libre_Baskerville] text-4xl text-[var(--gold)] mt-3">{value ?? '—'}</div>
    </Wrapper>
  );
};

const Modal = ({ open, onClose, title, children, footer, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="admin-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-[var(--bg-alt)] border border-[var(--gold-faint)] ${wide ? 'max-w-4xl' : 'max-w-2xl'} w-full max-h-[88vh] overflow-y-auto`}>
        <div className="sticky top-0 bg-[var(--bg-alt)] border-b border-[var(--gold-faint)] px-8 py-5 flex items-center justify-between z-10">
          <h2 className="font-[Libre_Baskerville] text-2xl text-[var(--ink)]">{title}</h2>
          <button data-testid="admin-modal-close" onClick={onClose} className="text-[var(--ink-muted)] hover:text-[var(--gold)] text-2xl leading-none">×</button>
        </div>
        <div className="p-8">{children}</div>
        {footer && <div className="sticky bottom-0 bg-[var(--bg-alt)] border-t border-[var(--gold-faint)] px-8 py-4 flex justify-end gap-3">{footer}</div>}
      </div>
    </div>
  );
};

const renderValue = (key, value) => {
  if (value == null || value === '') return <span className="text-[var(--ink-muted)]/50">—</span>;
  if (Array.isArray(value)) return value.length ? value.join(', ') : <span className="text-[var(--ink-muted)]/50">—</span>;
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (key === 'created_at') return fmtDate(value);
  if (key === 'case_study_deck_url' || key === 'brand_approval_url') {
    // R2 uploads are absolute (https://cdn.adauraawards.com/...); local-disk
    // fallback returns a relative /api/uploads/... path. Prepending
    // BACKEND_URL blindly produced malformed URLs like `.../api/https://...`
    // which broke the "Open file" link.
    const raw = String(value);
    const url = /^(https?:)?\/\//i.test(raw)
      ? raw
      : `${process.env.REACT_APP_BACKEND_URL}${raw.startsWith('/') ? '' : '/'}${raw}`;
    return <a href={url} target="_blank" rel="noreferrer" className="text-[var(--gold)] underline hover:text-[var(--gold-bright)]">Open file</a>;
  }
  if (key === 'website' || key === 'linkedin' || key === 'supporting_material_link') {
    return <a href={String(value).startsWith('http') ? value : `https://${value}`} target="_blank" rel="noreferrer" className="text-[var(--gold)] underline">{value}</a>;
  }
  return String(value);
};

const ViewBody = ({ row }) => {
  if (!row) return null;
  const entries = Object.entries(row).filter(([k]) => k !== '_id');
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-5">
      {entries.map(([k, v]) => (
        <div key={k} className={['campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'business_impact', 'campaign_objectives'].includes(k) ? 'md:col-span-2' : ''}>
          <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-1">{FRIENDLY_LABEL[k] || k}</div>
          <div className="text-[var(--ink)] text-sm whitespace-pre-wrap break-words">{renderValue(k, v)}</div>
        </div>
      ))}
    </div>
  );
};

const EditBody = ({ row, fields, onChange }) => {
  if (!row) return null;
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
      {fields.map((k) => {
        const isLong = ['campaign_summary', 'business_challenge', 'strategy_idea', 'execution', 'elevator_pitch', 'campaign_objectives', 'business_impact', 'previous_recognition', 'message'].includes(k);
        return (
          <div key={k} className={isLong ? 'md:col-span-2' : ''}>
            <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-1">{FRIENDLY_LABEL[k] || k}</label>
            {isLong ? (
              <textarea
                data-testid={`admin-edit-${k}`}
                value={row[k] ?? ''}
                onChange={(e) => onChange(k, e.target.value)}
                rows={4}
                className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none p-3 text-[var(--ink)] text-sm"
              />
            ) : (
              <input
                data-testid={`admin-edit-${k}`}
                value={row[k] ?? ''}
                onChange={(e) => onChange(k, e.target.value)}
                className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm"
              />
            )}
          </div>
        );
      })}
    </div>
  );
};

const DataTable = ({ rows, columns, onView, onEdit, onDelete, onMarkPaid, onSubmitOnBehalf, testIdPrefix,
                    selectable = true, selected = [], onSelectionChange }) => {
  const isEmpty = !rows.length;
  const selectedSet = new Set(selected || []);
  const visibleIds = rows.map((r) => r.id).filter(Boolean);
  const allVisibleSelected = selectable && visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = selectable && visibleIds.some((id) => selectedSet.has(id)) && !allVisibleSelected;

  const toggleAllVisible = (checked) => {
    if (!onSelectionChange) return;
    if (checked) {
      const next = new Set(selectedSet);
      visibleIds.forEach((id) => next.add(id));
      onSelectionChange(Array.from(next));
    } else {
      onSelectionChange(selected.filter((id) => !visibleIds.includes(id)));
    }
  };

  const toggleOne = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(Array.from(next));
  };

  return (
    <div className="overflow-x-auto border border-[var(--gold-faint)]">
      <table className="w-full text-sm" data-testid={`${testIdPrefix}-table`}>
        <thead className="bg-[var(--bg-deep)]/50">
          <tr>
            {selectable && (
              <th className="w-10 px-3 py-3 text-left">
                <input
                  type="checkbox"
                  data-testid={`${testIdPrefix}-select-all`}
                  aria-label="Select all on this page"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  disabled={isEmpty}
                  className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer disabled:opacity-40 disabled:cursor-not-allowed"
                />
              </th>
            )}
            {columns.map((c) => (
              <th key={c.key} className="text-left px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">{c.label}</th>
            ))}
            <th className="text-right px-4 py-3 text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)] font-normal">Actions</th>
          </tr>
        </thead>
        <tbody>
          {isEmpty ? (
            <tr>
              <td
                colSpan={columns.length + 1 + (selectable ? 1 : 0)}
                className="text-center py-16 text-[var(--ink-muted)] tracking-[0.05em]"
                data-testid={`${testIdPrefix}-empty`}
              >
                No entries yet.
              </td>
            </tr>
          ) : rows.map((r, i) => {
            const isOfflinePending = r.status === 'payment_pending' && (r.payment_mode || '') === 'offline' && r.payment_status !== 'paid';
            // Any draft (whether standalone or in a cart) can be submitted on
            // the entrant's behalf. Once submitted+paid we hide the action.
            const isDraft = !!onSubmitOnBehalf && (r.status || '').toLowerCase() === 'draft';
            const rowSelected = selectable && selectedSet.has(r.id);
            return (
            <tr key={r.id || i} className={`border-t border-[var(--gold-faint)]/40 hover:bg-[var(--gold-faint)]/10 ${rowSelected ? 'bg-[var(--gold-faint)]/15' : ''}`} data-testid={`${testIdPrefix}-row-${i}`}>
              {selectable && (
                <td className="w-10 px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    data-testid={`${testIdPrefix}-select-${i}`}
                    aria-label={`Select row ${i + 1}`}
                    checked={!!rowSelected}
                    onChange={() => toggleOne(r.id)}
                    className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer"
                  />
                </td>
              )}
              {columns.map((c) => (
                <td key={c.key} className="px-4 py-3 text-[var(--ink)] align-top max-w-[260px] truncate">{c.render ? c.render(r) : r[c.key] ?? '—'}</td>
              ))}
              <td className="px-4 py-3 text-right whitespace-nowrap">
                <button data-testid={`${testIdPrefix}-view-${i}`} onClick={() => onView(r)} className="text-[var(--gold)] hover:text-[var(--gold-bright)] text-xs tracking-[0.2em] uppercase mr-3">View</button>
                {onMarkPaid && isOfflinePending && (
                  <button data-testid={`${testIdPrefix}-mark-paid-${i}`} onClick={() => onMarkPaid(r)}
                          className="text-[#5C9F60] hover:text-[#3f7a43] text-xs tracking-[0.2em] uppercase mr-3 font-semibold">
                    ✓ Mark Paid
                  </button>
                )}
                {isDraft && (
                  <button data-testid={`${testIdPrefix}-submit-on-behalf-${i}`} onClick={() => onSubmitOnBehalf(r)}
                          className="text-[#5C9F60] hover:text-[#3f7a43] text-xs tracking-[0.2em] uppercase mr-3 font-semibold">
                    ✓ Submit
                  </button>
                )}
                {onEdit && <button data-testid={`${testIdPrefix}-edit-${i}`} onClick={() => onEdit(r)} className="text-[var(--ink)] hover:text-[var(--gold)] text-xs tracking-[0.2em] uppercase mr-3">Edit</button>}
                <button data-testid={`${testIdPrefix}-delete-${i}`} onClick={() => onDelete(r)} className="text-[var(--terracotta)] hover:opacity-80 text-xs tracking-[0.2em] uppercase">Delete</button>
              </td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

/**
 * Bulk-actions toolbar. Sticks to the top of the table when any row is
 * selected and offers the shared actions (currently: Delete Selected). New
 * bulk verbs (Mark Paid, Export, …) can be added by dropping another button
 * here plus wiring an onXxx prop.
 */
const BulkActionsBar = ({ count, resourceLabel, onDelete, onClear, testIdPrefix }) => {
  if (!count) return null;
  return (
    <div
      className="sticky top-[64px] z-20 mb-3 flex items-center justify-between gap-4 px-4 py-3 bg-[#3A2418] text-white border border-[#C9A56C]/40"
      data-testid={`${testIdPrefix}-bulk-bar`}
    >
      <div className="flex items-center gap-3">
        <span className="text-[11px] tracking-[0.3em] uppercase text-[#D9B26A]">Selection</span>
        <span className="text-sm font-semibold" data-testid={`${testIdPrefix}-bulk-count`}>
          {count} {resourceLabel}{count === 1 ? '' : 's'} selected
        </span>
      </div>
      <div className="flex items-center gap-2">
        <button
          type="button"
          onClick={onClear}
          className="text-[10px] tracking-[0.28em] uppercase px-3 py-1.5 text-white/80 hover:text-white transition-colors"
          data-testid={`${testIdPrefix}-bulk-clear`}
        >
          Clear
        </button>
        <button
          type="button"
          onClick={onDelete}
          className="text-[10px] tracking-[0.28em] uppercase px-4 py-1.5 bg-[#8B3A2E] text-white hover:bg-[#a04336] transition-colors font-semibold"
          data-testid={`${testIdPrefix}-bulk-delete`}
        >
          Delete Selected
        </button>
      </div>
    </div>
  );
};

/**
 * Registrations (Nominators) tab. Renders every nominator with per-user
 * aggregates (nominations, drafts, paid) plus a filter for "signed up but
 * never submitted" — the awards team uses this to reach out to stalled users.
 */
const NominatorsTable = ({ rows, filter, setFilter, search, selected = [], onSelectionChange }) => {
  const q = (search || '').trim().toLowerCase();
  const filtered = rows
    .filter((r) => {
      if (filter === 'not_submitted') return !r.has_submitted;
      if (filter === 'submitted') return r.has_submitted;
      return true;
    })
    .filter((r) => {
      if (!q) return true;
      return [r.name, r.email, r.phone, r.organisation].filter(Boolean).some((v) => String(v).toLowerCase().includes(q));
    });

  const selectedSet = new Set(selected || []);
  const visibleIds = filtered.map((r) => r.id).filter(Boolean);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedSet.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedSet.has(id)) && !allVisibleSelected;

  const toggleAllVisible = (checked) => {
    if (!onSelectionChange) return;
    if (checked) {
      const next = new Set(selectedSet);
      visibleIds.forEach((id) => next.add(id));
      onSelectionChange(Array.from(next));
    } else {
      onSelectionChange(selected.filter((id) => !visibleIds.includes(id)));
    }
  };
  const toggleOne = (id) => {
    if (!onSelectionChange) return;
    const next = new Set(selectedSet);
    if (next.has(id)) next.delete(id); else next.add(id);
    onSelectionChange(Array.from(next));
  };

  const exportCsv = () => {
    const rowsForCsv = filtered.map((r) => ({
      Name: r.name || '', Email: r.email || '', Phone: r.phone || '',
      Company: r.organisation || '',
      'Registered On': (r.created_at || '').slice(0, 10),
      'Last Login': (r.last_login_at || '').slice(0, 10),
      'Nominations': r.nominations_count || 0,
      'Submitted': r.submitted_count || 0,
      'Drafts': r.draft_count || 0,
      'Paid': r.paid_count || 0,
      'Payment Pending': r.payment_pending_count || 0,
      'Has Submitted': r.has_submitted ? 'Yes' : 'No',
    }));
    const header = Object.keys(rowsForCsv[0] || { Name: '' });
    const escape = (v) => {
      const s = String(v ?? '');
      return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const csv = [header.join(','), ...rowsForCsv.map((r) => header.map((k) => escape(r[k])).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `adaura-nominators-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
  };

  return (
    <div data-testid="admin-nominators">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="inline-flex text-[10px] tracking-[0.28em] uppercase">
          {[
            { k: 'all', label: `All (${rows.length})` },
            { k: 'not_submitted', label: `Yet to Submit (${rows.filter((r) => !r.has_submitted).length})` },
            { k: 'submitted', label: `Submitted (${rows.filter((r) => r.has_submitted).length})` },
          ].map((btn) => (
            <button key={btn.k} onClick={() => setFilter(btn.k)}
              className={`px-4 py-2 border ${filter === btn.k ? 'bg-[var(--gold)] text-[var(--bg)] border-[var(--gold)]' : 'bg-transparent text-[var(--ink)] border-[var(--gold-faint)] hover:border-[var(--gold)]'}`}
              data-testid={`nominators-filter-${btn.k}`}>
              {btn.label}
            </button>
          ))}
        </div>
        <button onClick={exportCsv} data-testid="nominators-export-csv"
          className="text-[10px] tracking-[0.28em] uppercase px-4 py-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--gold)]">
          Export CSV
        </button>
      </div>

      <div className="overflow-x-auto border border-[var(--gold-faint)] bg-[var(--bg-alt)]/30">
        <table className="w-full text-sm" data-testid="admin-nominators-table">
          <thead>
            <tr className="bg-[var(--bg-alt)] text-[10px] tracking-[0.25em] uppercase text-[var(--ink-muted)]">
              <th className="w-10 px-3 py-3 text-left">
                <input
                  type="checkbox"
                  data-testid="admin-nominators-select-all"
                  aria-label="Select all registrations on this page"
                  checked={allVisibleSelected}
                  ref={(el) => { if (el) el.indeterminate = someVisibleSelected; }}
                  onChange={(e) => toggleAllVisible(e.target.checked)}
                  className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer"
                />
              </th>
              <th className="px-4 py-3 text-left">Name</th>
              <th className="px-4 py-3 text-left">Email</th>
              <th className="px-4 py-3 text-left">Phone</th>
              <th className="px-4 py-3 text-left">Company</th>
              <th className="px-4 py-3 text-left">Registered</th>
              <th className="px-4 py-3 text-center">Nominations</th>
              <th className="px-4 py-3 text-center">Submitted</th>
              <th className="px-4 py-3 text-center">Paid</th>
              <th className="px-4 py-3 text-center">Status</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={10} className="text-center py-10 text-[var(--ink-muted)]">No registrations match this filter.</td></tr>
            )}
            {filtered.map((r, i) => {
              const rowSelected = selectedSet.has(r.id);
              return (
              <tr key={r.id || i} className={`border-t border-[var(--gold-faint)]/40 hover:bg-[var(--gold-faint)]/10 ${rowSelected ? 'bg-[var(--gold-faint)]/15' : ''}`} data-testid={`nominator-row-${i}`}>
                <td className="w-10 px-3 py-3 align-top">
                  <input
                    type="checkbox"
                    data-testid={`admin-nominators-select-${i}`}
                    aria-label={`Select registration ${i + 1}`}
                    checked={!!rowSelected}
                    onChange={() => toggleOne(r.id)}
                    className="w-4 h-4 accent-[var(--gold-deep)] cursor-pointer"
                  />
                </td>
                <td className="px-4 py-3 text-[var(--ink)]">{r.name || '—'}</td>
                <td className="px-4 py-3 text-[var(--ink)]">{r.email || '—'}</td>
                <td className="px-4 py-3 text-[var(--ink)]">{r.phone || '—'}</td>
                <td className="px-4 py-3 text-[var(--ink)]">{r.organisation || '—'}</td>
                <td className="px-4 py-3 text-[var(--ink-muted)] whitespace-nowrap">{(r.created_at || '').slice(0, 10) || '—'}</td>
                <td className="px-4 py-3 text-center text-[var(--ink)]">{r.nominations_count || 0}</td>
                <td className="px-4 py-3 text-center text-[var(--ink)]">{r.submitted_count || 0}</td>
                <td className="px-4 py-3 text-center text-[var(--ink)]">{r.paid_count || 0}</td>
                <td className="px-4 py-3 text-center">
                  {r.has_submitted
                    ? <span className="inline-block text-[10px] tracking-[0.24em] uppercase px-2 py-0.5 bg-[var(--gold)] text-[var(--bg)]">Active</span>
                    : <span className="inline-block text-[10px] tracking-[0.24em] uppercase px-2 py-0.5 border border-[var(--terracotta)] text-[var(--terracotta)]">Follow Up</span>}
                </td>
              </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="text-[var(--ink-muted)]/60 text-[10px] tracking-[0.25em] uppercase mt-4">
        Showing {filtered.length} of {rows.length} registrations
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  usePageTitle('Admin Panel');
  const { admin, logout } = useAuth();
  const navigate = useNavigate();

  const [tab, setTab] = useState('nominations');

  // If the initial tab is one the current admin can't access, jump to the
  // first tab they do have permission for. This runs once auth loads.
  useEffect(() => {
    if (!admin?.permissions) return;
    const visible = TABS.filter((t) => admin.permissions.includes(t.key));
    if (visible.length && !visible.some((t) => t.key === tab)) {
      setTab(visible[0].key);
    }
  }, [admin?.permissions]);

  const [stats, setStats] = useState({});
  const [nominations, setNominations] = useState([]);
  const [nominators, setNominators] = useState([]);
  const [nominatorsFilter, setNominatorsFilter] = useState('all'); // 'all' | 'not_submitted' | 'submitted'
  const [sponsors, setSponsors] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [search, setSearch] = useState('');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filters, setFilters] = useState({ month: '', category: '', status: '' });
  const [categoriesList, setCategoriesList] = useState([]);
  const [loading, setLoading] = useState(false);

  const [viewRow, setViewRow] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [editRow, setEditRow] = useState(null);
  // Row currently being marked-paid via the offline verification modal.
  // Shape matches the AdminPaymentsTab.MarkPaidModal contract.
  const [markPaidRow, setMarkPaidRow] = useState(null);
  const [submitOnBehalfRow, setSubmitOnBehalfRow] = useState(null);
  const [editDraft, setEditDraft] = useState({});
  const [deleteRow, setDeleteRow] = useState(null);
  const [saving, setSaving] = useState(false);
  // Bulk selection — keyed by row `id`. Cleared automatically whenever the
  // active tab changes so selection never bleeds across resources.
  const [selectedIds, setSelectedIds] = useState([]);
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [bulkDeleting, setBulkDeleting] = useState(false);

  useEffect(() => { setSelectedIds([]); }, [tab]);

  /* ------------------------------------------------------------------
   * Bulk delete — one shared handler for every listing that supports it.
   * Resource key is derived from the currently-active tab. Confirms in
   * a modal, hits the unified /admin/bulk-delete endpoint, then reloads
   * the whole dashboard so counts + subsequent tabs stay consistent.
   * ------------------------------------------------------------------ */
  const bulkDeletableResource = (() => {
    switch (tab) {
      case 'nominations':  return { resource: 'nominations',  label: 'nomination' };
      case 'nominators':   return { resource: 'nominators',   label: 'registration' };
      case 'sponsors':     return { resource: 'sponsors',     label: 'sponsor' };
      case 'subscribers':  return { resource: 'subscribers',  label: 'subscriber' };
      case 'contacts':     return { resource: 'contacts',     label: 'contact' };
      default: return null;
    }
  })();

  const performBulkDelete = async () => {
    if (!bulkDeletableResource || !selectedIds.length) return;
    setBulkDeleting(true);
    try {
      const { data } = await axiosAuth.post(`${API}/admin/bulk-delete`, {
        resource: bulkDeletableResource.resource,
        ids: selectedIds,
      });
      toast.success(`${data.deleted} ${bulkDeletableResource.label}${data.deleted === 1 ? '' : 's'} deleted.`);
      setSelectedIds([]);
      setBulkDeleteOpen(false);
      await loadAll();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Bulk delete failed.');
    } finally {
      setBulkDeleting(false);
    }
  };

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      // Build filter params for stats + nominations
      const params = {};
      if (filters.category) params.category = filters.category;
      if (filters.status) params.status = filters.status;
      if (filters.month) {
        // e.g. 'July' → start/end ISO range within current year (2026)
        const monthMap = { January:0,February:1,March:2,April:3,May:4,June:5,July:6,August:7,September:8,October:9,November:10,December:11 };
        const m = monthMap[filters.month];
        if (m !== undefined) {
          const year = new Date().getFullYear();
          const start = new Date(year, m, 1).toISOString();
          const end = new Date(year, m + 1, 0, 23, 59, 59).toISOString();
          params.start = start; params.end = end;
        }
      }
      const nomParams = {};
      if (filters.category) nomParams.category = filters.category;
      if (filters.status) nomParams.payment_status = filters.status === 'paid' ? 'paid' : undefined;

      const [s, n, nomtrs, sp, sub, ct, cats] = await Promise.all([
        axiosAuth.get(`${API}/admin/master-stats`, { params }),
        axiosAuth.get(`${API}/admin/nominations-filtered`, { params: nomParams }),
        axiosAuth.get(`${API}/admin/nominators`),
        axiosAuth.get(`${API}/admin/sponsors`),
        axiosAuth.get(`${API}/admin/subscribers`),
        axiosAuth.get(`${API}/admin/contacts`),
        axiosAuth.get(`${API}/admin/categories-list`),
      ]);
      setStats(s.data);
      setNominations(n.data);
      setNominators(nomtrs.data || []);
      setSponsors(sp.data);
      setSubscribers(sub.data);
      setContacts(ct.data);
      setCategoriesList(cats.data || []);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Failed to load admin data.');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => { loadAll(); }, [loadAll]);

  /* ------------------------------------------------------------------
   * View handler — ALWAYS fetches the freshest copy of the nomination
   * from the DB before opening the modal. Prior versions used the row
   * cached in local state at page-load, which meant admins saw stale
   * data whenever a nominator edited their entry from the Portal after
   * the admin dashboard had already loaded. Now every "View" click hits
   * `/admin/nominations/{id}` for a live read + patches the list row
   * in-place so the table also reflects the latest values. Falls back
   * gracefully to the cached row on network failure.
   * ------------------------------------------------------------------ */
  const openView = useCallback(async (row) => {
    if (tab !== 'nominations' || !row?.id) { setViewRow(row); return; }
    setViewRow(row);        // optimistic open with cached data
    setViewLoading(true);
    try {
      const { data: fresh } = await axiosAuth.get(`${API}/admin/nominations/${row.id}`);
      setViewRow(fresh);
      // Keep the table in sync so subsequent renders show current values.
      setNominations((list) => list.map((r) => (r.id === fresh.id ? { ...r, ...fresh } : r)));
    } catch (e) {
      // Silent — the modal still shows the (cached) row we already opened.
      // Only surface an error if the row genuinely no longer exists.
      if (e.response?.status === 404) {
        toast.error('This nomination no longer exists. Refreshing list…');
        setViewRow(null);
        loadAll();
      }
    } finally {
      setViewLoading(false);
    }
  }, [tab, loadAll]);

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login', { replace: true });
  };

  const filterRows = (rows) => {
    let out = rows;
    // Structured filters — only meaningful on the Nominations tab (where the
    // status / category / month fields exist). Applied FIRST so the drill-down
    // stat-cards on the dashboard land the admin on the right subset, and the
    // text-search box narrows further.
    if (tab === 'nominations') {
      if (filters.status)   out = out.filter((r) => (r.status || '').toLowerCase() === filters.status.toLowerCase() || (r.payment_status || '').toLowerCase() === filters.status.toLowerCase());
      if (filters.category) out = out.filter((r) => (r.category || '') === filters.category);
      if (filters.month)    out = out.filter((r) => (r.created_at || '').slice(0, 7) === filters.month); // YYYY-MM
    }
    if (!search.trim()) return out;
    const q = search.trim().toLowerCase();
    return out.filter((r) =>
      Object.values(r).some((v) => {
        if (v == null) return false;
        if (typeof v === 'object') return JSON.stringify(v).toLowerCase().includes(q);
        return String(v).toLowerCase().includes(q);
      })
    );
  };

  const openEdit = (row) => {
    setEditRow(row);
    setEditDraft({ ...row });
  };

  const saveEdit = async () => {
    if (!editRow) return;
    const editableFields =
      tab === 'nominations' ? NOMINATION_EDITABLE :
      tab === 'sponsors' ? SPONSOR_EDITABLE :
      tab === 'subscribers' ? SUBSCRIBER_EDITABLE : [];
    const updates = {};
    editableFields.forEach((k) => {
      if (editDraft[k] !== editRow[k]) updates[k] = editDraft[k];
    });
    if (!Object.keys(updates).length) {
      toast.info('No changes to save.');
      setEditRow(null);
      return;
    }
    setSaving(true);
    try {
      const { data } = await axiosAuth.patch(`${API}/admin/${tab}/${editRow.id}`, updates);
      if (tab === 'nominations') setNominations((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      if (tab === 'sponsors') setSponsors((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      if (tab === 'subscribers') setSubscribers((rs) => rs.map((r) => (r.id === data.id ? data : r)));
      toast.success('Updated.');
      setEditRow(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Update failed.');
    } finally {
      setSaving(false);
    }
  };

  const confirmDelete = async () => {
    if (!deleteRow) return;
    setSaving(true);
    try {
      await axiosAuth.delete(`${API}/admin/${tab}/${deleteRow.id}`);
      if (tab === 'nominations') setNominations((rs) => rs.filter((r) => r.id !== deleteRow.id));
      if (tab === 'sponsors') setSponsors((rs) => rs.filter((r) => r.id !== deleteRow.id));
      if (tab === 'subscribers') setSubscribers((rs) => rs.filter((r) => r.id !== deleteRow.id));
      if (tab === 'contacts') setContacts((rs) => rs.filter((r) => r.id !== deleteRow.id));
      toast.success('Deleted.');
      setDeleteRow(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Delete failed.');
    } finally {
      setSaving(false);
    }
  };

  const currentRows =
    tab === 'nominations' ? filterRows(nominations) :
    tab === 'sponsors' ? filterRows(sponsors) :
    tab === 'subscribers' ? filterRows(subscribers) :
    filterRows(contacts);

  const columns = {
    nominations: [
      { key: 'entry_no', label: 'Entry ID', render: (r) => (
        <div className="flex items-center gap-2">
          <span className="numeral text-[var(--gold)] text-xs font-bold">{r.entry_no != null ? String(r.entry_no).padStart(2, '0') : '—'}</span>
          {Array.isArray(r.sibling_entry_nos) && r.sibling_entry_nos.length > 1 && (
            <span
              title={`Linked submission group · Entries #${r.sibling_entry_nos.join(', #')} share one submission, one payment (${r.payment_id || 'unpaid'}).`}
              className="text-[9px] tracking-[0.22em] uppercase border border-[#A76B3E] text-[#A76B3E] px-1.5 py-0.5"
              data-testid={`submission-group-${r.submission_id}`}
            >
              +{r.sibling_entry_nos.length - 1} linked
            </span>
          )}
        </div>
      ) },
      { key: 'pillar', label: 'Category', render: (r) => r.pillar_name || r.category || '—' },
      { key: 'sub_category', label: 'Sub Category', render: (r) => r.sub_category || '—' },
      { key: 'entry_title', label: 'Campaign', render: (r) => r.entry_title || '—' },
      { key: 'brand_name', label: 'Brand', render: (r) => r.brand_name || '—' },
      { key: 'submitter', label: 'Submitted By', render: (r) => (
        // Task-1 requirement: submitter details separate from campaign details.
        // Falls back to the legacy contact fields for pre-multi-category rows.
        <div className="leading-tight">
          <div className="text-[var(--ink)]">{r.submitter_name || `${r.first_name || ''} ${r.last_name || ''}`.trim() || '—'}</div>
          <div className="text-[10.5px] text-[var(--ink-soft)]">{r.submitter_company || '—'}</div>
          <div className="text-[10px] text-[var(--ink-soft)]">{r.submitter_email || r.email || ''}{r.submitter_mobile ? ` · ${r.submitter_mobile}` : ''}</div>
        </div>
      ) },
      { key: 'organisation_name', label: 'Entrant Organisation', render: (r) => r.organisation_name || '—' },
      { key: 'designation', label: 'Designation', render: (r) => r.designation || '—' },
      { key: 'email', label: 'Email' },
      { key: 'mobile', label: 'Mobile', render: (r) => r.mobile || '—' },
      { key: 'status', label: 'Status', render: (r) => {
        const s = r.status || 'draft';
        const isOffline = (r.payment_mode || '') === 'offline';
        const meta =
          s === 'submitted' ? { c: '#7BC47F', l: 'Submitted · Paid' }
          : s === 'payment_pending' && isOffline ? { c: '#C2613A', l: 'Submitted · Offline' }
          : s === 'payment_pending' ? { c: '#E0A659', l: 'Submitted · Pmt Due' }
          : { c: 'var(--ink-muted)', l: 'Draft' };
        return <span className="text-[10px] tracking-[0.2em] uppercase font-semibold" style={{ color: meta.c }}>{meta.l}</span>;
      } },
      { key: 'payment_status', label: 'Payment Status', render: (r) => {
        const s = r.payment_status === 'paid' ? { c: '#7BC47F', l: 'Paid' } : r.status === 'payment_pending' ? { c: '#E0A659', l: 'Pending' } : { c: 'var(--ink-muted)', l: 'Unpaid' };
        return <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: s.c }}>{s.l}</span>;
      } },
      { key: 'jury_status', label: 'Jury Status', render: (r) => {
        const j = r.jury_status === 'scored' ? { c: '#7BC47F', l: `Scored${r.jury_avg != null ? ' ' + r.jury_avg : ''}` } : r.jury_status === 'in_review' ? { c: '#E0A659', l: 'In Review' } : { c: 'var(--ink-muted)', l: 'Pending' };
        return <span className="text-[10px] tracking-[0.2em] uppercase" style={{ color: j.c }}>{j.l}</span>;
      } },
      { key: 'shortlisted', label: 'Shortlisted', render: (r) => r.shortlisted ? <span className="text-[#C9A56C] text-[10px] tracking-[0.2em] uppercase">★ Yes</span> : <span className="text-[var(--ink-muted)] text-[10px]">—</span> },
    ],
    sponsors: [
      { key: 'full_name', label: 'Name' },
      { key: 'company', label: 'Company' },
      { key: 'interest', label: 'Interest' },
      { key: 'email', label: 'Email' },
      { key: 'phone', label: 'Phone' },
      { key: 'created_at', label: 'Submitted', render: (r) => fmtDate(r.created_at) },
    ],
    subscribers: [
      { key: 'handle', label: 'Handle' },
      { key: 'channel', label: 'Channel' },
      { key: 'created_at', label: 'Submitted', render: (r) => fmtDate(r.created_at) },
    ],
    contacts: [
      { key: 'name', label: 'Name' },
      { key: 'email', label: 'Email' },
      { key: 'purpose', label: 'Purpose' },
      { key: 'message', label: 'Message', render: (r) => (r.message || '').slice(0, 80) },
      { key: 'created_at', label: 'Submitted', render: (r) => fmtDate(r.created_at) },
    ],
  };

  const editableFor = tab === 'nominations' ? NOMINATION_EDITABLE : tab === 'sponsors' ? SPONSOR_EDITABLE : tab === 'subscribers' ? SUBSCRIBER_EDITABLE : null;

  return (
    <div className="min-h-screen bg-[var(--bg)] text-[var(--ink)]" data-testid="admin-dashboard">
      {/* Header — dark brown editorial band */}
      <div className="border-b border-[#B8884A]/30 bg-[#2D1810] sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-5 flex items-center justify-between">
          <div>
            <div className="text-[10px] tracking-[0.5em] uppercase text-[#D9B26A]">AdAura Awards 2026</div>
            <div className="font-[Libre_Baskerville] text-2xl text-white mt-1">Control Panel</div>
            {typeof window !== 'undefined' && (() => {
              const host = window.location.host;
              const isLive = /adauraawards\.com$/i.test(host);
              const label = isLive ? 'LIVE' : 'PREVIEW';
              const tone = isLive
                ? { c: '#7BC47F', bg: 'rgba(123,196,127,0.12)', bd: 'rgba(123,196,127,0.4)' }
                : { c: '#E0A659', bg: 'rgba(224,166,89,0.12)', bd: 'rgba(224,166,89,0.4)' };
              return (
                <a
                  href="https://adauraawards.com/admin"
                  target={isLive ? '_self' : '_blank'}
                  rel="noreferrer"
                  data-testid="admin-env-chip"
                  className="inline-flex items-center gap-2 mt-2 px-2.5 py-1 border text-[9.5px] tracking-[0.25em] uppercase transition-colors"
                  style={{ color: tone.c, borderColor: tone.bd, background: tone.bg }}
                  title={isLive ? 'You are on the live site.' : 'You are on the preview environment — click to open the live admin panel.'}
                >
                  <span style={{ width: 6, height: 6, background: tone.c, borderRadius: '50%' }} />
                  {label} · {host}
                </a>
              );
            })()}
          </div>
          <div className="flex items-center gap-5">
            <div className="text-right hidden md:block">
              <div className="text-white text-sm" data-testid="admin-email">{admin?.email}</div>
              <div className="text-white/55 text-[10px] tracking-[0.2em] uppercase">{admin?.role}</div>
            </div>
            <button
              data-testid="admin-logout-btn"
              onClick={handleLogout}
              className="border border-[#B8884A]/45 hover:border-[#D9B26A] text-white/65 hover:text-[#D9B26A] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
            >
              Log out
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        {/* Stats Header — title + filter trigger */}
        <div className="flex items-center justify-between mb-5">
          <div className="text-[10px] tracking-[0.5em] uppercase text-[var(--gold)]">Master Overview</div>
          <div className="relative">
            <button data-testid="admin-filter-btn" onClick={() => setFilterOpen((v) => !v)} className="inline-flex items-center gap-2 border border-[var(--gold-faint)] hover:border-[var(--gold)] hover:text-[var(--gold)] text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M3 5h18M6 12h12M10 19h4"/></svg>
              Filters {(filters.month || filters.category || filters.status) && <span className="w-1.5 h-1.5 rounded-full bg-[var(--gold)]" />}
            </button>
            {filterOpen && (
              <div className="absolute right-0 top-12 z-40 w-80 bg-[var(--bg-alt)] border border-[var(--gold-faint)] p-5 space-y-4 shadow-2xl" data-testid="admin-filter-panel">
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Date Range — Month</label>
                  <select data-testid="filter-month" value={filters.month} onChange={(e) => setFilters((f) => ({ ...f, month: e.target.value }))} className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
                    <option value="">All months</option>
                    {['January','February','March','April','May','June','July','August','September','October','November','December'].map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Category</label>
                  <select data-testid="filter-category" value={filters.category} onChange={(e) => setFilters((f) => ({ ...f, category: e.target.value }))} className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
                    <option value="">All categories</option>
                    {categoriesList.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-2">Status</label>
                  <select data-testid="filter-status" value={filters.status} onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))} className="w-full bg-[var(--bg)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm" style={{ colorScheme: 'dark' }}>
                    <option value="">All statuses</option>
                    <option value="submitted">Submitted</option>
                    <option value="draft">Draft</option>
                    <option value="payment_pending">Payment Pending</option>
                    <option value="paid">Paid</option>
                    <option value="awaiting_review">Awaiting Review</option>
                    <option value="under_jury">Under Jury</option>
                    <option value="shortlisted">Shortlisted</option>
                    <option value="gold">Gold</option>
                    <option value="silver">Silver</option>
                    <option value="bronze">Bronze</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button data-testid="filter-clear" onClick={() => { setFilters({ month: '', category: '', status: '' }); setFilterOpen(false); }} className="text-[var(--ink-muted)] text-[10px] tracking-[0.3em] uppercase px-3 py-2">Clear</button>
                  <button data-testid="filter-apply" onClick={() => setFilterOpen(false)} className="bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2">Apply</button>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Master Stats — 11 metrics. Cards are clickable — clicking one
            applies the corresponding filter to the entries table and scrolls
            it into view so the admin sees the drilled-down list immediately. */}
        {(() => {
          const drill = (patch) => {
            // Cards drill into the Nominations tab. Switch tab first so the
            // filter targets the visible table.
            setTab('nominations');
            setFilters((f) => ({ ...f, ...patch }));
            setFilterOpen(false);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="admin-nominations-table"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 60);
          };
          // "Total Registrations" opens the dedicated Nominators tab so the
          // team can see participants who signed up but never nominated.
          const drillNominators = (nomFilter = 'all') => {
            setTab('nominators');
            setNominatorsFilter(nomFilter);
            setTimeout(() => {
              const el = document.querySelector('[data-testid="admin-nominators-table"]');
              if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }, 60);
          };
          return (
            <>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3 mb-4">
                <StatCard label="Total Registrations" value={stats.total_registrations} testId="stat-registrations" onClick={() => drillNominators('all')} />
                <StatCard label="Total Entries Submitted" value={stats.total_submitted} testId="stat-submitted" onClick={() => drill({ status: 'submitted', category: '', month: '' })} />
                <StatCard label="Draft Entries" value={stats.draft_entries} testId="stat-drafts" onClick={() => drill({ status: 'draft', category: '', month: '' })} />
                <StatCard label="Payment Pending" value={stats.pending_payments} testId="stat-pending" onClick={() => drill({ status: 'payment_pending', category: '', month: '' })} />
                <StatCard label="Paid" value={stats.successful_payments} testId="stat-paid" onClick={() => drill({ status: 'paid', category: '', month: '' })} />
                <StatCard label="Shortlisted" value={stats.shortlisted_entries} testId="stat-shortlisted" onClick={() => drill({ status: 'shortlisted', category: '', month: '' })} />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-10">
                <StatCard label="Awaiting Review" value={stats.awaiting_review} testId="stat-awaiting" onClick={() => drill({ status: 'awaiting_review', category: '', month: '' })} />
                <StatCard label="Under Jury" value={stats.under_jury_review} testId="stat-under-jury" onClick={() => drill({ status: 'under_jury', category: '', month: '' })} />
                <StatCard label="Gold" value={stats.gold} testId="stat-gold" onClick={() => drill({ status: 'gold', category: '', month: '' })} />
                <StatCard label="Silver" value={stats.silver} testId="stat-silver" onClick={() => drill({ status: 'silver', category: '', month: '' })} />
                <StatCard label="Bronze" value={stats.bronze} testId="stat-bronze" onClick={() => drill({ status: 'bronze', category: '', month: '' })} />
              </div>
            </>
          );
        })()}

        {/* Top Categories mini-list */}
        {stats.top_categories?.length > 0 && (
          <div className="mb-10 border border-[var(--gold-faint)] p-5" data-testid="top-categories">
            <div className="text-[10px] tracking-[0.3em] uppercase text-[var(--ink-muted)] mb-3">Categories with most entries</div>
            <div className="flex flex-wrap gap-3">
              {stats.top_categories.map((c, i) => (
                <div key={c.category} className="flex items-center gap-2 border border-[var(--gold-faint)]/40 px-3 py-1.5">
                  <span className="numeral text-[var(--gold)] text-xs">{i + 1}</span>
                  <span className="text-[var(--ink)] text-sm">{c.category}</span>
                  <span className="text-[var(--ink-muted)] text-xs">· {c.count}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Tabs — filtered to those the current admin has permission for.
             `admin.permissions` comes from /auth/me and includes every
             section a super_admin can access. */}
        <div className="flex flex-wrap items-end justify-between gap-4 border-b border-[var(--gold-faint)] mb-6">
          <div className="flex flex-wrap">
            {TABS.filter((t) => !admin?.permissions || admin.permissions.includes(t.key)).map((t) => (
              <button
                key={t.key}
                data-testid={`tab-${t.key}`}
                onClick={() => { setTab(t.key); setSearch(''); }}
                className={`px-5 py-3 text-xs tracking-[0.3em] uppercase border-b-2 -mb-px transition-colors ${
                  tab === t.key ? 'border-[var(--gold)] text-[var(--gold)]' : 'border-transparent text-[var(--ink-muted)] hover:text-[var(--ink)]'
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-3 pb-2">
            {!CUSTOM_TABS.has(tab) && (
              <>
                <input
                  data-testid="admin-search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Search…"
                  className="bg-[var(--bg-alt)] border border-[var(--gold-faint)] focus:border-[var(--gold)] outline-none px-3 py-2 text-[var(--ink)] text-sm w-56"
                />
                <button
                  data-testid="admin-export-csv"
                  onClick={() => csvExport(`adaura-${tab}-${new Date().toISOString().slice(0,10)}.csv`, currentRows)}
                  className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
                >
                  CSV
                </button>
                <button
                  data-testid="admin-export-xlsx"
                  onClick={() => downloadXLSX(`adaura-${tab}-${new Date().toISOString().slice(0,10)}.xlsx`, currentRows, tab)}
                  className="border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold)] hover:text-[var(--bg)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
                >
                  Excel
                </button>
              </>
            )}
            <button
              data-testid="admin-refresh"
              onClick={loadAll}
              className="border border-[var(--gold-faint)] hover:border-[var(--gold)] text-[var(--ink-muted)] hover:text-[var(--gold)] text-[10px] tracking-[0.3em] uppercase px-4 py-2 transition-colors"
            >
              Refresh
            </button>
          </div>
        </div>

        {tab === 'jurors' ? (
          <AdminJurorsTab />
        ) : tab === 'categories' ? (
          <AdminCategoriesTab />
        ) : tab === 'payments' ? (
          <AdminPaymentsTab />
        ) : tab === 'scoring' ? (
          <AdminScoringTab />
        ) : tab === 'frameworks' ? (
          <AdminFrameworksTab />
        ) : tab === 'settings' ? (
          <AdminSettingsTab />
        ) : tab === 'cms' ? (
          <AdminCMSTab />
        ) : tab === 'winners' ? (
          <AdminWinnersTab />
        ) : tab === 'admins' ? (
          <AdminUsersTab />
        ) : tab === 'nominators' ? (
          <>
            <BulkActionsBar
              count={selectedIds.length}
              resourceLabel="registration"
              onDelete={() => setBulkDeleteOpen(true)}
              onClear={() => setSelectedIds([])}
              testIdPrefix="admin-nominators"
            />
            <NominatorsTable
              rows={nominators}
              filter={nominatorsFilter}
              setFilter={setNominatorsFilter}
              search={search}
              selected={selectedIds}
              onSelectionChange={setSelectedIds}
            />
          </>
        ) : loading ? (
          <div className="text-center py-20 text-[var(--ink-muted)] tracking-[0.3em] uppercase text-xs" data-testid="admin-loading">Loading…</div>
        ) : (
          <>
            {bulkDeletableResource && (
              <BulkActionsBar
                count={selectedIds.length}
                resourceLabel={bulkDeletableResource.label}
                onDelete={() => setBulkDeleteOpen(true)}
                onClear={() => setSelectedIds([])}
                testIdPrefix={`admin-${tab}`}
              />
            )}
            <DataTable
              rows={currentRows}
              columns={columns[tab]}
              onView={openView}
              onEdit={editableFor ? openEdit : null}
              onDelete={setDeleteRow}
              onMarkPaid={tab === 'nominations' ? (r) => setMarkPaidRow({
                // Adapt Nomination row into MarkPaidModal's payment-row shape.
                entry_id: r.id,
                entry_no: r.entry_no,
                campaign: r.entry_title,
                company: r.organisation_name,
                amount_paise: r.payment_amount_paise || 0,
                submission_id: r.submission_id,
              }) : null}
              onSubmitOnBehalf={tab === 'nominations' ? (r) => setSubmitOnBehalfRow(r) : null}
              testIdPrefix={`admin-${tab}`}
              selectable={!!bulkDeletableResource}
              selected={selectedIds}
              onSelectionChange={setSelectedIds}
            />
          </>
        )}

        {!CUSTOM_TABS.has(tab) && (
          <div className="text-[var(--ink-muted)]/60 text-[10px] tracking-[0.25em] uppercase mt-6">
            Showing {currentRows.length} {tab}
          </div>
        )}
      </div>

      {/* View Modal */}
      <Modal open={!!viewRow} onClose={() => setViewRow(null)}
             title={`${tab.slice(0, -1).replace(/^./, (c) => c.toUpperCase())} Detail`}
             wide
             footer={tab === 'nominations' && viewRow ? (
               <>
                 <a
                   href={`${BACKEND_URL}/api/admin/nominations/${viewRow.id}/export.pdf`}
                   target="_blank"
                   rel="noopener noreferrer"
                   data-testid="admin-download-nomination-pdf"
                   className="inline-flex items-center gap-2 bg-[#3A2418] text-white px-5 py-2.5 text-[10.5px] tracking-[0.24em] uppercase font-semibold hover:bg-[#4E3B2A] transition-colors"
                 >
                   Download Nomination as PDF
                 </a>
                 <a
                   href={`${BACKEND_URL}/api/admin/nominations/${viewRow.id}/export`}
                   target="_blank"
                   rel="noopener noreferrer"
                   data-testid="admin-view-nomination-html"
                   className="inline-flex items-center gap-2 border border-[#3A2418] text-[#3A2418] px-5 py-2.5 text-[10.5px] tracking-[0.24em] uppercase font-semibold hover:bg-[#3A2418] hover:text-white transition-colors ml-3"
                 >
                   Preview in Browser
                 </a>
               </>
             ) : null}
      >
        {viewLoading && tab === 'nominations' && (
          <div className="mb-3 -mt-2 flex items-center gap-2 text-[10px] tracking-[0.28em] uppercase text-[var(--gold-deep)]" data-testid="admin-view-refreshing">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-[var(--gold-deep)] animate-pulse" />
            Syncing latest edits…
          </div>
        )}
        <ViewBody row={viewRow} />
      </Modal>

      {/* Edit Modal */}
      <Modal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        title={`Edit ${tab.slice(0, -1)}`}
        wide
        footer={
          <>
            <button data-testid="admin-edit-cancel" onClick={() => setEditRow(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
            <button data-testid="admin-edit-save" onClick={saveEdit} disabled={saving} className="bg-[var(--gold)] hover:bg-[var(--gold-bright)] text-[var(--bg)] text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">
              {saving ? 'Saving…' : 'Save Changes'}
            </button>
          </>
        }
      >
        {editableFor && <EditBody row={editDraft} fields={editableFor} onChange={(k, v) => setEditDraft((d) => ({ ...d, [k]: v }))} />}
        {tab === 'nominations' && editRow && (
          <AdminSupportingFiles
            nomination={editDraft}
            onChange={(updated) => {
              // Uploads persist immediately, so sync BOTH the working copy
              // (so the modal reflects the new file) and the source row (so
              // "Cancel" doesn't visually revert an already-saved upload).
              setEditDraft((prev) => ({ ...prev, ...updated }));
              setEditRow((prev) => ({ ...prev, ...updated }));
            }}
          />
        )}
      </Modal>

      {/* Delete Confirm */}
      <Modal
        open={!!deleteRow}
        onClose={() => setDeleteRow(null)}
        title="Confirm deletion"
        footer={
          <>
            <button data-testid="admin-delete-cancel" onClick={() => setDeleteRow(null)} className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2">Cancel</button>
            <button data-testid="admin-delete-confirm" onClick={confirmDelete} disabled={saving} className="bg-[var(--terracotta)] hover:opacity-90 text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50">
              {saving ? 'Deleting…' : 'Delete permanently'}
            </button>
          </>
        }
      >
        <p className="text-[var(--ink)] text-sm">
          This will permanently remove this {tab.slice(0, -1)} from the database. This action cannot be undone.
        </p>
        {deleteRow && (
          <div className="mt-4 text-[var(--ink-muted)] text-xs space-y-1">
            <div>ID: <span className="text-[var(--ink)]">{deleteRow.id}</span></div>
            {deleteRow.email && <div>Email: <span className="text-[var(--ink)]">{deleteRow.email}</span></div>}
            {deleteRow.handle && <div>Handle: <span className="text-[var(--ink)]">{deleteRow.handle}</span></div>}
            {deleteRow.entry_title && <div>Entry: <span className="text-[var(--ink)]">{deleteRow.entry_title}</span></div>}
          </div>
        )}
      </Modal>

      {/* Bulk Delete Confirm */}
      <Modal
        open={bulkDeleteOpen}
        onClose={() => (bulkDeleting ? null : setBulkDeleteOpen(false))}
        title="Delete selected entries?"
        footer={
          <>
            <button
              data-testid="admin-bulk-delete-cancel"
              onClick={() => setBulkDeleteOpen(false)}
              disabled={bulkDeleting}
              className="text-[var(--ink-muted)] text-xs tracking-[0.2em] uppercase px-4 py-2 disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              data-testid="admin-bulk-delete-confirm"
              onClick={performBulkDelete}
              disabled={bulkDeleting}
              className="bg-[#8B3A2E] hover:bg-[#a04336] text-white text-xs tracking-[0.2em] uppercase px-6 py-2 disabled:opacity-50 font-semibold"
            >
              {bulkDeleting ? 'Deleting…' : `Delete ${selectedIds.length}`}
            </button>
          </>
        }
      >
        <p className="text-[var(--ink)] text-sm">
          You are about to permanently delete{' '}
          <strong>{selectedIds.length}</strong>{' '}
          {bulkDeletableResource?.label}{selectedIds.length === 1 ? '' : 's'} from the database.
          This action cannot be undone.
        </p>
        <p className="text-[var(--ink-muted)] text-xs mt-3 tracking-[0.05em]">
          Tip: use bulk delete only to clean up junk / test entries. Individual
          rows with active payments should be handled one at a time so any
          linked Razorpay receipts are archived first.
        </p>
      </Modal>
      {markPaidRow && (
        <MarkPaidModal
          row={markPaidRow}
          onClose={() => setMarkPaidRow(null)}
          onSuccess={() => { setMarkPaidRow(null); loadAll(); toast.success('Nomination marked paid.'); }}
        />
      )}
      {submitOnBehalfRow && (
        <AdminSubmitOnBehalfModal
          nomination={submitOnBehalfRow}
          onClose={() => setSubmitOnBehalfRow(null)}
          onSubmitted={loadAll}
        />
      )}
    </div>
  );
};

export default AdminDashboard;
